import { Tabs, type TabsRootProps } from "@heroui/react";
import { type AppTabsVariant } from "./variants";

/**
 * Constrained tabs root props.
 * className escape hatch available for one-off overrides.
 */
export type AppTabsProps = Omit<TabsRootProps, "className" | "variant"> & {
  /** Visual variant. @default "primary" */
  variant?: AppTabsVariant;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppTabs root — Design system tabbed container.
 */
function AppTabsRoot({
  variant = "primary",
  className,
  ...props
}: AppTabsProps) {
  return <Tabs {...props} variant={variant} className={className} />;
}

/**
 * AppTabs — Design system tabbed navigation.
 *
 * Wraps HeroUI v3 Tabs with constrained Rosé Pine variants.
 * Sub-components (TabListContainer, TabList, Tab, TabIndicator, TabPanel)
 * are HeroUI pass-throughs.
 *
 * @example
 * ```tsx
 * <AppTabs variant="primary">
 *   <AppTabs.ListContainer>
 *     <AppTabs.List aria-label="Settings">
 *       <AppTabs.Tab id="general">General</AppTabs.Tab>
 *       <AppTabs.Tab id="security">Security</AppTabs.Tab>
 *     </AppTabs.List>
 *     <AppTabs.Indicator />
 *   </AppTabs.ListContainer>
 *   <AppTabs.Panel id="general">General settings...</AppTabs.Panel>
 *   <AppTabs.Panel id="security">Security settings...</AppTabs.Panel>
 * </AppTabs>
 * ```
 */
export const AppTabs = Object.assign(AppTabsRoot, {
  ListContainer: Tabs.ListContainer,
  List: Tabs.List,
  Tab: Tabs.Tab,
  Indicator: Tabs.Indicator,
  Panel: Tabs.Panel,
});
